import { BodyM, Caption, Small } from '@casavo/base-ui'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import { Dispatch, PropsWithChildren, SetStateAction, useEffect } from 'react'

import { Floor } from '@/components/Floor'
import { ModalBase } from '@/components/Modal/ModalBase'
import { Translated } from '@/components/Translated'
import { useModal } from '@/context/ModalContext'
import { useCurrentTenant } from '@/context/TenantProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/style-theme/theme.css'
import { TENANTS } from '@/lib/tenants/constants'
import { formatListingPricePerSquareMeter } from '@/utils/format-listing-pricePerSquareMeter'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'
import { getPriceStatus } from '@/utils/utils-potential-properties'

import { borderLeft, priorityMainInfo, priorityMainInfoHeader } from '../../../styles.css'
import { ModalPrice } from '../ModalPrice/ModalPrice'
import { modalPriceWidth } from '../ModalPrice/ModalPrice.css'
import { PriceBadge } from '../PriceBadge'

type Props = {
  disableVisitCta: boolean
  handleRequestVisitCtaBuyerInfo: VoidFunction
  hasBuyerInfo: boolean
  price: {
    askedPrice: { cents: number }
    estimatedListingPrice: { cents: number }
  }
  priority: {
    buildingFloors: number
    commercialArea: number
    entranceFloor: number
    transactionId: string
  }
  setShowToastError: Dispatch<SetStateAction<boolean>>
  showAlreadyBookedBanner: boolean
  thankYouPageUrl: string
}

export const PriorityPriceInfo: React.FC<Props> = ({
  disableVisitCta,
  handleRequestVisitCtaBuyerInfo,
  hasBuyerInfo,
  price,
  priority,
  setShowToastError,
  showAlreadyBookedBanner,
  thankYouPageUrl,
}) => {
  const { lang, t } = useTranslation()
  const tenant = useCurrentTenant()

  const { buildingFloors, commercialArea, entranceFloor, transactionId } = priority
  const { askedPrice, estimatedListingPrice } = price
  const { isGoodPrice, roundedDiscountedPrice } = getPriceStatus(askedPrice.cents, estimatedListingPrice.cents)

  const fsProperty = isGoodPrice ? 'GoodPrice' : 'HighPrice'
  const description = isGoodPrice
    ? 'priority:PriceAnalysis_GoodPrice_Description'
    : 'priority:PriceAnalysis_HighPrice_Description'
  const { openModal } = useModal()

  const handleCtaClick = () => {
    handleClick(GTMEventCategory.PriorityDetail, 'PriceAnalysisCTA', fsProperty)
    openModal(
      <ModalBase className={modalPriceWidth}>
        <ModalPrice
          disableVisitCta={disableVisitCta}
          handleRequestVisitCtaBuyerInfo={handleRequestVisitCtaBuyerInfo}
          hasBuyerInfo={hasBuyerInfo}
          isSuccess={isGoodPrice}
          price={{
            askedPrice: askedPrice,
            discountedPrice: roundedDiscountedPrice,
            estimatedListingPrice: formatListingSellingPrice(false, estimatedListingPrice, t, lang),
          }}
          setShowToastError={setShowToastError}
          showAlreadyBookedBanner={showAlreadyBookedBanner}
          thankYouPageUrl={thankYouPageUrl}
          transactionId={transactionId}
        />
      </ModalBase>,
      {
        background: theme.colors.brandedModalBackground,
        borderRadius: theme.border.brandedModalBorderRadius,
        overlay: true,
      }
    )
  }

  useEffect(() => {
    handleShow(GTMEventCategory.PriorityDetail, 'PriceAnalysisBanner', fsProperty)
  }, [])

  return (
    <PriceInfoContainer handleClick={tenant !== 'ucsc' ? handleCtaClick : undefined}>
      <div className={priorityMainInfoHeader[tenant]}>
        <div>
          {Boolean(commercialArea) && (
            <BodyM
              strong
              alignment="center"
              as="p"
              className={sprinkles({
                paddingBottom: { mobile: 'xs' },
              })}
            >
              {commercialArea} m&sup2;
            </BodyM>
          )}
          <Small alignment="center" color="greyscale500">
            <Floor
              buildingFloors={buildingFloors}
              entireBuilding={false}
              entranceFloor={entranceFloor}
              floorPosition={[]}
            />
          </Small>
        </div>

        {askedPrice && (
          <div className={cn(borderLeft, sprinkles({ alignSelf: 'center' }))}>
            <BodyM
              strong
              alignment="center"
              className={sprinkles({
                paddingBottom: { mobile: 'xs' },
              })}
            >
              {formatListingSellingPrice(false, askedPrice, t, lang)}
            </BodyM>

            {Boolean(commercialArea) && (
              <Caption alignment="center" color="greyscale500">
                {formatListingPricePerSquareMeter(askedPrice, commercialArea, lang)}
              </Caption>
            )}
          </div>
        )}

        {tenant === TENANTS.casavo && (
          <div className={cn(borderLeft)}>
            <PriceBadge isSuccess={isGoodPrice} />
          </div>
        )}
      </div>

      <Caption className={sprinkles({ alignSelf: 'center', textAlign: 'left' })}>
        <Translated
          components={{
            u: <span className={sprinkles({ textDecoration: 'underline' })} />,
          }}
          i18Key={tenant === TENANTS.ucsc ? 'unicredit:PriceAnalysis_Description' : description}
        />
      </Caption>
    </PriceInfoContainer>
  )
}

const PriceInfoContainer: React.FC<PropsWithChildren<{ handleClick?: () => void }>> = ({ children, handleClick }) => {
  if (handleClick) {
    return (
      <button className={priorityMainInfo} onClick={handleClick}>
        {children}
      </button>
    )
  }
  return <div className={priorityMainInfo}>{children}</div>
}
